import { useContext } from "react";
import type { ValidationError } from "@tanstack/react-form";
import { useForm } from "@tanstack/react-form";
import { isAxiosError } from "axios";
import { Link, useNavigate } from "react-router-dom";

import { useLogin } from "@/api/auth";
import {
  InsideLabelFieldInput,
  InsideLabelFieldPassword,
  SubscribeButton,
} from "@/components/form";
import AuthPageTitle from "@/components/layout/auth-page-title";
import { AuthContext } from "@/contexts/auth-context";
import { emailSchema, loginDefaultValues, loginSchema } from "@/schemas/auth";
import { SubscriptionStatus } from "@/types/api/user";
import { stopPropagation } from "@/utils/form";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login: performLogin } = useContext(AuthContext);
  const { mutate } = useLogin();

  const { Field, Subscribe, handleSubmit } = useForm({
    defaultValues: loginDefaultValues,
    validators: {
      onSubmit: ({ value }) => {
        const { email } = value;
        const returnValue: { fields: { [k: string]: ValidationError } } = {
          fields: {},
        };
        if (!emailSchema.safeParse(email).success) {
          const emailError: ValidationError = "Veuillez entrer un email valide";
          returnValue.fields["email"] = emailError;
        }
        return returnValue;
      },
    },
    onSubmit: async ({ value, formApi }) => {
      mutate(value, {
        onSuccess: (data) => {
          performLogin(data);
          if (data.user.subscriptionStatus === SubscriptionStatus.ACTIVE) {
            navigate("/");
          } else {
            navigate("/subscription");
          }
        },
        onError: (e) => {
          if (isAxiosError(e)) {
            if (e.response?.data.message === "WRONG_CREDENTIALS") {
              formApi.setFieldMeta("password", (prev) => ({
                ...prev,
                errors: [
                  "L'email et/ou le mot de passe utilisés sont incorrects.",
                ],
                errorMap: {
                  onChange:
                    "L'email et/ou le mot de passe utilisés sont incorrects.",
                },
              }));
              return;
            }
          }
        },
      });
    },
  });

  return (
    <form
      onSubmit={(e) => stopPropagation(e, handleSubmit)}
      className="grid grid-flow-row grid-cols-1 gap-12 pr-[112px] pl-[112px] w-full"
    >
      <AuthPageTitle
        title="Connectez-vous à votre compte"
        subTitle="Entrez vos informations pour accéder à votre espace."
      />
      <div>
        <div className="flex flex-col gap-4">
          <InsideLabelFieldInput
            Field={Field}
            name="email"
            label="Email"
            schema={loginSchema}
            autoComplete="username"
            validators={{
              onChange: loginSchema.shape.email,
            }}
          />
          <InsideLabelFieldPassword
            Field={Field}
            name="password"
            label="Mot de passe"
            schema={loginSchema}
            autoComplete="current-password"
            validators={{
              onChange: loginSchema.shape.password,
            }}
          />
        </div>

        <Link to="/mot-de-passe-oublie" className="mt-4 inline-block">
          <p className="text-sm text-semibold bg-[linear-gradient(-90deg,_#2C00B7_0%,_#B5307D_67%,_#FC9CAC_100%)] bg-clip-text text-transparent">
            Mot de passe oublié
          </p>
        </Link>
      </div>
      <div className="flex flex-col gap-8">
        <SubscribeButton Subscribe={Subscribe}>Se connecter</SubscribeButton>

        <div className="flex items-center justify-center w-full gap-2">
          <p className="text-sm">Pas encore de compte ?</p>
          <Link to="/inscription">
            <span className="text-sm text-semibold bg-[linear-gradient(-90deg,_#2C00B7_0%,_#B5307D_67%,_#FC9CAC_100%)] bg-clip-text text-transparent">
              Inscrivez-vous
            </span>
          </Link>
        </div>
      </div>
    </form>
  );
};

export default LoginPage;
